import React, { useEffect, useState } from 'react';
import { Alert, AlertIcon, Text } from '@chakra-ui/react';

/** Below this, the hold is close enough to expiry to warrant a warning. */
const URGENT_SECONDS = 120;

/**
 * Parses the server's hold expiry into ms, or null when nothing is held.
 *
 * The server sends one timestamp — the earliest expiry across the basket —
 * rather than a seat per line item, so this stays a straight parse.
 */
export const parseExpiry = (expiresAt?: unknown): number | null => {
  if (!expiresAt) return null;
  const time = new Date(expiresAt as string).getTime();
  return Number.isNaN(time) ? null : time;
};

export const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

interface Props {
  /** `Basket.seatHoldExpiresAt` — earliest hold expiry, or null/undefined. */
  expiresAt?: unknown;
  /** Injectable for tests; defaults to the real clock. */
  now?: () => number;
}

/**
 * Shows how long the event seats in a basket are reserved for.
 *
 * Seats are claimed at add-time, so a basket is a real reservation with a
 * deadline. Without surfacing it, a customer would simply find their tickets
 * gone at checkout with no explanation — the hold exists to be fair to them,
 * and it can only feel fair if they can see it.
 */
const SeatHoldCountdown = ({ expiresAt, now = Date.now }: Props) => {
  const expiry = parseExpiry(expiresAt);
  const [remaining, setRemaining] = useState<number>(expiry ? expiry - now() : 0);

  useEffect(() => {
    if (!expiry) return undefined;
    setRemaining(expiry - now());
    const timer = setInterval(() => setRemaining(expiry - now()), 1000);
    return () => clearInterval(timer);
  }, [expiry, now]);

  if (!expiry) return null;

  if (remaining <= 0) {
    return (
      <Alert status="error" borderRadius="md" fontSize="sm">
        <AlertIcon />
        <Text>Your seats are no longer reserved. Refresh to check availability.</Text>
      </Alert>
    );
  }

  const urgent = remaining <= URGENT_SECONDS * 1000;

  return (
    <Alert status={urgent ? 'warning' : 'info'} borderRadius="md" fontSize="sm">
      <AlertIcon />
      <Text>
        {'Your seats are reserved for '}
        <strong>{formatRemaining(remaining)}</strong>
      </Text>
    </Alert>
  );
};

export default SeatHoldCountdown;
